import * as state from '@/components/Analysis/state';
import { PageLoader } from '@/components/shared/PageLoader';
import { StructureCheck } from '@/components/shared/StructureCheck';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Box, Stack } from '@mui/material';
import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { NoReportFound } from '../shared/NoReportFound';
import { ReportInternalType, useReportDetailsQuery } from '@/__generated__/graphql';
import { Container } from './styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { AccessGuard } from '@/components/Analysis/shared/AccessGuard';
import { useRouter } from '@/hooks';
import { PoiReportContainer } from './ReportContainers/PoiReportContainer';
import { PaintBlockReportContainer } from './ReportContainers/PaintBlockReportContainer';
import { ComponentReportContainer } from './ReportContainers/ComponentReportContainer';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { ReportHeader } from './ReportHeader';
import { UpgradePrompt } from '../../shared/UpgradePrompt';
import { UpgradePromptBackground } from '@/types';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';

export const ReportDetails = () => {
  const router = useRouter();
  const reportId = router?.query?.reportid;
  const setSelectedReport = useSetAtom(state.selectedReport);
  const setAllPointOfInterestTemplates = useSetAtom(state.allPointOfInterestTemplates);

  const { data: poiTemplates } = useFetchTemplatesByType();
  const { rightDrawerWidth } = useDrawerWidths();
  const navbarHeight = useNavbarHeight();
  const structureId = useGetStructureId();

  const { data, loading, error } = useReportDetailsQuery({
    variables: {
      input: {
        reportId: typeof reportId === 'string' ? reportId : '',
      },
    },
    skip: !reportId,
    onCompleted: (reponse) => {
      const report = reponse?.report;

      if (report) {
        setSelectedReport(report);
      }
    },
  });

  useEffect(() => {
    // This is to ensure that state data is refreshed
    // after the 'refetch' event is triggered
    if (data?.report) {
      setSelectedReport(data.report);
    }
  }, [data?.report, setSelectedReport]);

  useEffect(() => {
    const allTemplates = poiTemplates?.allPointOfInterestTemplate;
    if (!allTemplates) return;

    setAllPointOfInterestTemplates(allTemplates);
  }, [poiTemplates?.allPointOfInterestTemplate, setAllPointOfInterestTemplates]);

  const internalType = data?.report.internalType;

  if (!structureId || structureId === '') {
    return <div>Please specify inspection id</div>;
  }

  const dataIsEmpty = (error && !loading) || (!data?.report && !loading);

  return (
    <StructureCheck structureId={structureId}>
      <AccessGuard structureId={structureId} feature="reports">
        <UpgradePrompt
          feature="reports"
          featureTitle="Reports"
          background={UpgradePromptBackground.Reports}
          paragraphs={[
            'Access enhanced reports with automated site images',
            'View marked location of anomalies',
            'Export reports for streamlined tracking, seamless monitoring, and documentation purposes',
            'Add selected images of anomalies for greater clarity',
          ]}
        >
          {loading ? (
            <Stack direction="row" justifyContent="center" marginTop={30}>
              <PageLoader />
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="center">
              <Container navbarHeight={navbarHeight} data-testid="report-container">
                <Box
                  marginRight={`${rightDrawerWidth}px`}
                  width={`calc(100vw - ${rightDrawerWidth}px)`}
                >
                  {internalType === ReportInternalType.PaintBlock && <PaintBlockReportContainer />}
                  {internalType === ReportInternalType.PointOfInterest && (
                    <>
                      <ReportHeader />
                      <PoiReportContainer />
                    </>
                  )}
                  {internalType === ReportInternalType.Component && (
                    <>
                      <ReportHeader />
                      <ComponentReportContainer />
                    </>
                  )}
                </Box>
              </Container>
            </Stack>
          )}
          {dataIsEmpty && <NoReportFound />}
        </UpgradePrompt>
      </AccessGuard>
    </StructureCheck>
  );
};
